import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Page,
  Card,
  IndexTable,
  Badge,
  Button,
  ButtonGroup,
  Banner,
  BlockStack,
  InlineStack,
  Select,
  Spinner,
  Text,
  TextField,
  Tooltip,
  useIndexResourceState,
} from "@shopify/polaris";
import { api, ApiRequestError, isReauthError } from "../api";
import type {
  BacklogOrder,
  BacklogResponse,
  BuyAndPrintResult,
  InternalOrderStatus,
  OriginStatus,
  ShopMode,
} from "../types";
import StatusBadge from "../components/StatusBadge";
import EmptyState from "../components/EmptyState";
import FailureNotice from "../components/FailureNotice";
import { deliveryMethodBadge, isShippable } from "../lib/delivery-method";
import { shipByBadge, compareShipByUrgency } from "../lib/amazon-ship-by";
import { summarizeBuyAndPrint } from "../lib/buy-and-print-summary";
import posthog from "../posthog";

type Chip = "all" | "new" | "queued" | "skipped" | "failed";

type Sort = "newest" | "ship_by";

const SORT_OPTIONS = [
  { label: "Newest first", value: "newest" },
  { label: "Amazon ship-by urgency (earliest deadline first)", value: "ship_by" },
];

const CHIPS: Array<{ id: Chip; label: string }> = [
  { id: "all", label: "All" },
  { id: "new", label: "New" },
  { id: "queued", label: "Queued" },
  { id: "skipped", label: "Skipped" },
  { id: "failed", label: "Failed" },
];

// Anything that is neither untouched, waiting in the manual queue, nor
// deliberately left alone got as far as an attempt that didn't finish, so it
// belongs under Failed where the merchant looks for things to fix.
function chipFor(status: InternalOrderStatus): Exclude<Chip, "all"> {
  if (status === "new_backlog") return "new";
  if (status === "queued") return "queued";
  if (status === "skipped") return "skipped";
  return "failed";
}

function formatWeight(ounces: number | null): string {
  if (ounces === null || ounces === 0) return "—";
  return ounces >= 16 ? `${(ounces / 16).toFixed(2)} lb` : `${ounces.toFixed(1)} oz`;
}

function formatAddress(order: BacklogOrder): string {
  const parts = [order.ship_to.city, order.ship_to.state, order.ship_to.country].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "—";
}

function itemCount(order: BacklogOrder): number {
  return order.items.reduce((sum, item) => sum + item.quantity, 0);
}

export default function Orders(): JSX.Element {
  const [orders, setOrders] = useState<BacklogOrder[]>([]);
  const [mode, setMode] = useState<ShopMode>("auto");
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<BuyAndPrintResult[]>([]);
  const [chip, setChip] = useState<Chip>("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<Sort>("newest");
  const [origin, setOrigin] = useState<OriginStatus | null>(null);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    // Fired alongside the backlog fetch, never awaited: the pre-flight banner
    // is advisory and the orders list must not wait on a Shopify GraphQL
    // round-trip for it. Any failure means "don't know", and an advisory that
    // isn't sure says nothing rather than crying wolf.
    void api
      .get<OriginStatus>(`/origin-status${refresh ? "?refresh=1" : ""}`)
      .then(setOrigin)
      .catch(() => setOrigin(null));
    try {
      const response = await api.get<BacklogResponse>(`/orders/unfulfilled${refresh ? "?refresh=1" : ""}`);
      setOrders(response.orders);
      setMode(response.mode);
      setHasMore(response.has_more);
    } catch (err) {
      // The app shell already shows this one, with the button that fixes it.
      // Repeating it here as plain red text just adds noise above that banner.
      if (!isReauthError(err)) {
        setError(err instanceof ApiRequestError ? err.body.message : "Could not load orders");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(() => {
    const tally: Record<Chip, number> = { all: orders.length, new: 0, queued: 0, skipped: 0, failed: 0 };
    for (const order of orders) tally[chipFor(order.internal_status)]++;
    return tally;
  }, [orders]);

  // The API already returns newest-first, so that sort is the identity.
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matching = orders.filter((order) => {
      if (chip !== "all" && chipFor(order.internal_status) !== chip) return false;
      if (!needle) return true;
      return (
        order.name.toLowerCase().includes(needle) ||
        (order.customer_name ?? "").toLowerCase().includes(needle)
      );
    });
    if (sort === "newest") return matching;
    return [...matching].sort((a, b) =>
      compareShipByUrgency(a.amazon_latest_ship_date, b.amazon_latest_ship_date)
    );
  }, [orders, chip, search, sort]);

  // IndexTable selection is keyed on the resource id, so the legacy Shopify
  // order id is used directly — it is what the buy-and-print endpoint takes.
  const resources = useMemo(
    () => visible.map((order) => ({ ...order, id: order.shopify_order_id })),
    [visible]
  );
  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
    useIndexResourceState(resources);

  async function buyAndPrint(orderIds: string[]): Promise<void> {
    if (orderIds.length === 0) return;
    setBusy(true);
    setError(null);
    setResults([]);
    try {
      const response = await api.post<{ results: BuyAndPrintResult[] }>("/orders/buy-and-print", {
        // Deduped here as well as on the server so a stale selection can never
        // ask for the same order twice.
        order_ids: [...new Set(orderIds)],
      });
      setResults(response.results);
      posthog.capture("labels_buy_and_print_requested", { order_count: orderIds.length });
      clearSelection();
      await load(true);
    } catch (err) {
      if (!isReauthError(err)) {
        setError(err instanceof ApiRequestError ? err.body.message : "Could not buy labels");
      }
    } finally {
      setBusy(false);
    }
  }

  // Puts a label the merchant has already paid for back on the roll. Separate
  // from Buy + Print because it cannot spend money.
  async function reprint(jobId: number): Promise<void> {
    setBusy(true);
    setError(null);
    setResults([]);
    try {
      await api.post(`/print-jobs/${jobId}/reprint`);
      posthog.capture("label_reprint_requested", { source: "orders" });
      await load(true);
    } catch (err) {
      if (!isReauthError(err)) {
        setError(err instanceof ApiRequestError ? err.body.message : "Could not reprint the label");
      }
    } finally {
      setBusy(false);
    }
  }

  const selectedOrders = visible.filter((order) => selectedResources.includes(order.shopify_order_id));
  const selectedWeight = selectedOrders.reduce((sum, order) => sum + (order.total_weight_oz ?? 0), 0);
  // A bulk selection can sweep up an in-store collection, or an order whose
  // postage is already paid for. Both are dropped from the buy rather than sent
  // and skipped, so the count on the button is the number of labels that will
  // actually be bought — and the merchant cannot pay twice for the second kind.
  const buyable = selectedOrders.filter(
    (order) => isShippable(order.delivery_method_type) && !order.has_postage
  );
  const failures = results.filter((result) => !result.ok);
  const summary = summarizeBuyAndPrint(results);

  const now = new Date();

  const rows = resources.map((order, index) => {
    const badge = deliveryMethodBadge(order.delivery_method_type);
    const shipBy = shipByBadge(order.amazon_latest_ship_date, now);
    const shippable = isShippable(order.delivery_method_type);
    const buyButton = (
      <Button
        size="slim"
        disabled={busy || !shippable}
        onClick={() => {
          void buyAndPrint([order.shopify_order_id]);
        }}
      >
        Buy + Print
      </Button>
    );

    // Postage already paid for. Offering "Buy + Print" here is what charged
    // merchants twice, so the only action on these rows is a reprint.
    const reprintButton = (
      <Button
        size="slim"
        disabled={busy || order.job_id === null}
        onClick={() => {
          if (order.job_id !== null) void reprint(order.job_id);
        }}
      >
        Reprint label
      </Button>
    );

    return (
      <IndexTable.Row id={order.id} key={order.id} position={index} selected={selectedResources.includes(order.id)}>
        <IndexTable.Cell>
          <BlockStack gap="050">
            <Text as="span" fontWeight="semibold">
              {order.name}
            </Text>
            {shipBy ? <Badge tone={shipBy.tone}>{shipBy.label}</Badge> : null}
          </BlockStack>
        </IndexTable.Cell>
        <IndexTable.Cell>{new Date(order.created_at).toLocaleDateString()}</IndexTable.Cell>
        <IndexTable.Cell>{order.customer_name ?? "—"}</IndexTable.Cell>
        <IndexTable.Cell>{itemCount(order)}</IndexTable.Cell>
        <IndexTable.Cell>{formatWeight(order.total_weight_oz)}</IndexTable.Cell>
        <IndexTable.Cell>{formatAddress(order)}</IndexTable.Cell>
        <IndexTable.Cell>{badge ? <Badge tone={badge.tone}>{badge.label}</Badge> : null}</IndexTable.Cell>
        {/* Which carrier won the rate shop. Null until a label is bought, which
            is most rows in this queue — an em dash there is the honest answer,
            not missing data. */}
        <IndexTable.Cell>
          {order.carrier ? (
            <BlockStack gap="050">
              <Text as="span" variant="bodySm">
                {order.carrier}
              </Text>
              {order.service_level ? (
                <Text as="span" tone="subdued" variant="bodySm">
                  {order.service_level}
                </Text>
              ) : null}
            </BlockStack>
          ) : (
            "—"
          )}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <BlockStack gap="050">
            <StatusBadge status={order.internal_status} />
            {order.has_postage ? (
              <Text as="span" tone="subdued" variant="bodySm">
                Postage already paid for. Reprint. Do not buy again.
              </Text>
            ) : null}
            {/* A deliberate skip is a right answer, so it is not shown in red. */}
            {order.skip_reason ? (
              <Text as="span" tone="subdued" variant="bodySm" breakWord>
                {order.skip_reason}
              </Text>
            ) : null}
            {order.last_error ? (
              <FailureNotice error={order.last_error} detail={order.last_error_detail} />
            ) : null}
          </BlockStack>
        </IndexTable.Cell>
        <IndexTable.Cell>
          {order.has_postage ? (
            <Tooltip content="ShipPromise already bought this label. Reprinting costs nothing; buying again would charge you a second time.">
              <span>{reprintButton}</span>
            </Tooltip>
          ) : shippable ? (
            buyButton
          ) : (
            <Tooltip content="This order is not being shipped, so there is no label to buy.">
              <span>{buyButton}</span>
            </Tooltip>
          )}
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Page
      title="Orders"
      subtitle={
        mode === "manual"
          ? "Every paid order Shopify has not shipped yet"
          : "Auto mode is on. New orders print themselves. Anything left here needs a hand."
      }
      primaryAction={{
        content: `Buy + Print Selected${buyable.length > 0 ? ` (${buyable.length})` : ""}`,
        disabled: buyable.length === 0 || busy,
        loading: busy,
        onAction: () => {
          void buyAndPrint(buyable.map((order) => order.shopify_order_id));
        },
      }}
      secondaryActions={[
        { content: "Refresh from Shopify", onAction: () => void load(true), disabled: busy || loading },
      ]}
    >
      <BlockStack gap="400">
        {error && <Banner tone="critical">{error}</Banner>}

        {/* The problem behind the App Store rejection, said out loud before the
            first order fails instead of after. USPS and FedEx rate US origins
            only, so a shop address outside the US (or missing entirely) means
            every label attempt dies in rate shopping. Warning, not critical:
            purchases resolve ship-from per fulfillment order, so a merchant
            can still ship from a US location this shop-level check never saw. */}
        {origin && !origin.us_origin && (
          <Banner
            tone="warning"
            title={
              origin.configured
                ? "Your shipping origin is outside the US"
                : "Your shop's origin address is incomplete"
            }
          >
            <Text as="p">
              {origin.configured
                ? `Your shop address is in ${origin.country}. USPS and FedEx only quote shipments that start in the US, so label purchases will fail until an international carrier such as DHL Express is enabled on your EasyPost account, or your origin is a US address. Run the test rate shop in ShipPromise Settings to confirm carriers can quote your origin.`
                : "Carriers need a full street address, city, ZIP code and country to quote a rate, and your shop address is missing at least one of them. Complete it under Settings, then General, in your Shopify admin."}
            </Text>
          </Banner>
        )}

        {hasMore && (
          <Banner tone="warning">
            Showing the 500 most recent unfulfilled orders. Older ones are not listed.
          </Banner>
        )}

        {!loading && (
          <Text as="p" tone="subdued" variant="bodySm">
            Shopify only shares the last 60 days of orders with apps holding the standard order
            permission, so anything older than that will not appear here.
          </Text>
        )}

        {summary && (
          <Banner tone={summary.tone}>
            <BlockStack gap="100">
              <Text as="p">{summary.message}</Text>
              {summary.detail ? (
                <Text as="p" tone="subdued" variant="bodySm">
                  {summary.detail}
                </Text>
              ) : null}
            </BlockStack>
          </Banner>
        )}

        {failures.length > 0 && (
          <Banner tone="critical" title={`${failures.length} order(s) could not be processed`}>
            <BlockStack gap="100">
              {failures.map((failure) => (
                <Text as="p" key={failure.order_id}>
                  {failure.orderName}: {failure.error ?? "Unknown error"}
                </Text>
              ))}
            </BlockStack>
          </Banner>
        )}

        <Card>
          <InlineStack gap="300" align="space-between" blockAlign="center">
            <ButtonGroup variant="segmented">
              {CHIPS.map((option) => (
                <Button
                  key={option.id}
                  pressed={chip === option.id}
                  onClick={() => {
                    setChip(option.id);
                    clearSelection();
                  }}
                >
                  {`${option.label} (${counts[option.id]})`}
                </Button>
              ))}
            </ButtonGroup>
            <InlineStack gap="200" blockAlign="center">
              <div style={{ minWidth: 260 }}>
                <Select
                  label="Sort orders"
                  labelHidden
                  options={SORT_OPTIONS}
                  value={sort}
                  onChange={(value) => setSort(value as Sort)}
                />
              </div>
              <div style={{ minWidth: 260 }}>
                <TextField
                  label="Search orders"
                  labelHidden
                  placeholder="Search order number or customer"
                  value={search}
                  onChange={setSearch}
                  clearButton
                  onClearButtonClick={() => setSearch("")}
                  autoComplete="off"
                />
              </div>
            </InlineStack>
          </InlineStack>
        </Card>

        {loading ? (
          <Spinner accessibilityLabel="Loading orders" size="large" />
        ) : visible.length === 0 ? (
          <EmptyState
            heading={orders.length === 0 ? "No unfulfilled orders" : "No orders match this filter"}
          />
        ) : (
          <Card padding="0">
            <IndexTable
              resourceName={{ singular: "order", plural: "orders" }}
              itemCount={resources.length}
              selectedItemsCount={allResourcesSelected ? "All" : selectedResources.length}
              onSelectionChange={handleSelectionChange}
              headings={[
                { title: "Order" },
                { title: "Date" },
                { title: "Customer" },
                { title: "Items" },
                { title: "Weight" },
                { title: "Ship to" },
                { title: "Delivery" },
                { title: "Shipped via" },
                { title: "Status" },
                { title: "Actions" },
              ]}
            >
              {rows}
            </IndexTable>
            <div style={{ padding: "1rem", borderTop: "1px solid var(--p-color-border)" }}>
              <InlineStack align="space-between">
                <Text as="span" tone="subdued">
                  {selectedResources.length} selected
                </Text>
                <Text as="span" tone="subdued">
                  Total weight: {formatWeight(selectedWeight > 0 ? selectedWeight : null)}
                </Text>
              </InlineStack>
            </div>
          </Card>
        )}
      </BlockStack>
    </Page>
  );
}
